import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  Button,
  Divider,
  FormControlLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import upiIcon from '../../../assets/qr.png';
import cashIcon from '../../../assets/cash.png';
import QRCodePaymentScreen from './qr-payment.tsx';
import UnableToDeliverModal from './unable-to-deliver-modal.tsx';
import Heading from '../../Components/heading.tsx';
import { useNavigate, useLocation } from 'react-router-dom';
import { editOrders, getItems, editItems } from './action.ts';
import { toast } from 'react-toastify';

interface OrderSummaryScreenProps {
  deliveryStatus: string;
  onClose: () => void;
  paymentType?: 'Prepaid' | 'COD';
  orderId: string;
  warehouseId: number;
  vehicleId?: number;
}
interface OrderDetails {
  id: string;
  order_id: string;
  name: string;
  address: string;
  pincode: string;
  order_type: string;
  payment_mode: string;
  amount: number;
  [key: string]: any;
}
interface Item {
  id: string;
  name: string;
  qty: number; 
  rate: number; 
}

const OrderSummaryScreen: React.FC<OrderSummaryScreenProps> = ({
  deliveryStatus,
  onClose,
  orderId,
  warehouseId,
  vehicleId,
}) => {
  const [paymentMode, setPaymentMode] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [amountPaid, setAmountPaid] = useState('');
 const [modalOpen, setModalOpen] = useState(false);
const [modalType, setModalType] = useState<"unable" | "partial" | null>(null);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [itemsChecked, setItemsChecked] = useState<{ [key: string]: boolean }>({});
  const [selectedItemsTotal, setSelectedItemsTotal] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state as OrderDetails | null;
  const [urlParams, setUrlParams] = useState({
    token: '',
    userId: '',
    vehicleId: ''
  });
  
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    setUrlParams({
      token: queryParams.get('token') || '',
      userId: queryParams.get('user_id') || '',
      vehicleId: queryParams.get('vehicle_id') || ''
    });
  }, []);
  
  const getPaymentMode = (order) => {
    if (order.order?.cod !== undefined) {
      return order.order.cod === 1 ? 'COD' : 'Prepaid';
    }
    if (order.cod !== undefined) {
      return order.cod === 1 ? 'COD' : 'Prepaid';
    }
    return 'Unknown';
  };
  
  useEffect(() => {
    setPaymentType(getPaymentMode(order));    
    fetchItems();
  }, [order?.order?.id, order?.id]);

  const fetchItems = () => {
    setItemsLoading(true);
    getItems(
      100, 
      0, 
      order?.order?.id || order?.id,
      (response) => {
        const fetchedItems = response.message?.item_details_list || [];
        const formattedItems = fetchedItems.map((item: any) => ({
          id: item.id.toString(),
          name: item.name,
          qty: item.quantity,
          rate: item.invoice_per_item
        }));
        
        setItems(formattedItems);
        
        const initialCheckedState: { [key: string]: boolean } = {};
        formattedItems.forEach((item: Item) => {
          initialCheckedState[item.id] = order?.status || order?.order?.status === 'Delivered';
        });
        setItemsChecked(initialCheckedState);
        setItemsLoading(false);
      },
      (error) => {
        toast.error(`Failed to load items: ${error}`);
        setItemsLoading(false);
      }
    );
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.rate * item.qty), 0);

  useEffect(() => {
    const sum = items.reduce((total, item) => {
      return itemsChecked[item.id] ? total + (item.rate * item.qty) : total;
    }, 0);
    setSelectedItemsTotal(sum);
  }, [itemsChecked, items]);

  useEffect(() => {
    if (location.state?.fromQR) {
      handleAmountReceived(location.state.upiAmount, location.state.cashAmount);
    }
  }, [location]);

  const handleCheckboxChange = (itemId: string) => {
    if (order?.status || order?.order?.status !== 'Delivered') {
      setItemsChecked((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
    }
  };

  const allItemsChecked = items.length > 0 && Object.values(itemsChecked).every(Boolean);
  const someItemsChecked = Object.values(itemsChecked).some(Boolean) && !allItemsChecked;
  const noItemsChecked = items.length > 0 && Object.values(itemsChecked).every(checked => !checked);

const updateItemsStatusForAttempted = async (deliveredStatus: string, attemptedStatus: string) => {
  try {
    const updatePromises = items.map(item => {
      const status = itemsChecked[item.id] ? deliveredStatus : attemptedStatus;
      return new Promise<void>((resolve, reject) => {
        editItems(
          item.id,
          status,
          () => resolve(),
          (error) => reject(error)
        );
      });
    });

    await Promise.all(updatePromises);
    toast.success("Items updated successfully");
  } catch (error) {
    toast.error("Some items failed to update");
  }
};
const handleUnableToDeliverSubmit = async (selectedReason: string) => {
  try {
    setIsLoading(true);
    const status = selectedReason === "Customer Cancelled" ? "Cancelled" : "Attempted";

    if (modalType === "unable") {
      await editOrdersAPI(status, 0, 0, selectedReason);
      await updateItemsStatusForAttempted("Attempted", "Attempted");
    } else {
      await editOrdersAPI("Partially Delivered", 0, 0, selectedReason);
      await updateItemsStatusForAttempted("Delivered", "Attempted");
    }

    setModalOpen(false);
    navigate(
      `/wv/orders?vehicle_id=${urlParams.vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}`
    );
  } catch (error) {
    toast.error("Failed to update order status");
  } finally {
    setIsLoading(false);
  }
};

  const updateItemsStatus = async () => {
    try {
      const checkedItemIds = Object.keys(itemsChecked).filter(id => itemsChecked[id]);
      const updatePromises = checkedItemIds.map(itemId => 
        new Promise<void>((resolve, reject) => {
          editItems(
            itemId,
            'Delivered',
            (response) => {
              resolve();
            },
            (error) => {
              reject(error);
            }
          );
        })
      );
      
      await Promise.all(updatePromises);
      toast.success("All items updated successfully");
    } catch (error) {
      toast.error("Some items failed to update");
    }
  };

  const handleDelivered = async () => {
    if (noItemsChecked) {
      setModalOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      
      if (allItemsChecked) {
        if (paymentType === 'Prepaid') {
          await editOrdersAPI('Delivered');
          await updateItemsStatus();
          navigate(`/wv/orders?vehicle_id=${urlParams.vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}`);
        } else if (paymentMode.includes('Cash') && balance <= 0) {
const cappedCashAmount = Math.min(paidAmount, selectedItemsTotal);

          await editOrdersAPI('Delivered', cappedCashAmount, 0);
          await updateItemsStatus();
          navigate(`/wv/orders?vehicle_id=${urlParams.vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}`);
        }
else if (paymentMode.includes('UPI')) {
          const qrAmount = paymentMode.includes('Cash') ? Math.max(balance, 0) : selectedItemsTotal;
          navigate(`/wv/qr-payment?user_id=${urlParams.userId}&vehicle_id=${urlParams.vehicleId}&token=${urlParams.token}`, {
            state: {
              amount: qrAmount,
              cashPaid: paymentMode.includes('Cash') ? paidAmount : 0,
              order,
              warehouseId,
              vehicleId,
              items: items.map(item => ({
                ...item,
                checked: itemsChecked[item.id]
              }))
            }
          });
        } else {
          toast.error("Please collect full payment before marking as delivered");
        }
      } else if (someItemsChecked) {
         if (paymentMode.includes('UPI')) {
          const qrAmount = paymentMode.includes('Cash') ? Math.max(balance, 0) : selectedItemsTotal;
          navigateToQRPayment(qrAmount);
        } else if (paymentMode.includes('Cash') && balance <= 0) {
  const cappedCashAmount = Math.min(paidAmount, selectedItemsTotal);
  await editOrdersAPI('Partially Delivered', cappedCashAmount, 0);
  await updateItemsStatus();
  navigate(`/wv/orders?vehicle_id=${urlParams.vehicleId}&user_id=${urlParams.userId}&token=${urlParams.token}`);
}
else {
          toast.error("Please collect full payment before marking as partially delivered");
        }
      }
    } catch (error) {
      toast.error("Failed to process delivery");
    } finally {
      setIsLoading(false);
    }
  };
const handlePartialDelivery = () => {
  if (allItemsChecked) {
    handleDelivered();
  } else if (someItemsChecked) {
    if (paymentType === 'Prepaid') {
      setModalType("partial");
      setModalOpen(true);
    } else if (paymentType === 'COD') {
      if (paymentMode.includes('Cash') && balance <= 0) {
        setModalType("partial");
        setModalOpen(true);
      } else if (paymentMode.includes('Cash') && balance > 0) {
        if (paymentMode.includes('UPI')) {
          const qrAmount = balance;
          navigateToQRPayment(qrAmount);
        } else {
          toast.error("Please Collect Full amount");
        }
      } else if (paymentMode.includes('UPI')) {
        const qrAmount = selectedItemsTotal;
        navigateToQRPayment(qrAmount);
      } else {
        toast.error("Please select a payment method");
      }
    }
  }
};


const navigateToQRPayment = (qrAmount: number) => {
    navigate(`/wv/qr-payment?user_id=${urlParams.userId}&vehicle_id=${urlParams.vehicleId}&token=${urlParams.token}`, {
      state: {
        amount: qrAmount,
        cashPaid: paymentMode.includes('Cash') ? paidAmount : 0,
        order,
        warehouseId,
        vehicleId,
        items: items.map(item => ({
          ...item,
          checked: itemsChecked[item.id]
        })),
        isPartialDelivery: someItemsChecked
      }
    });
  };
  const handleAmountReceived = async (upiAmount: number, cashAmount: number) => {
    try {
      setIsLoading(true);
      await editOrdersAPI('Delivered', cashAmount, upiAmount);
      await updateItemsStatus();
      onClose();
    } catch (error) {
      toast.error("Failed to confirm payment");
    } finally {
      setIsLoading(false);
    }
  };

  const editOrdersAPI = async (
    status: string,
    amount_cash: number = 0,
    amount_upi: number = 0,
    remarks: string = ''
  ) => {
    return new Promise<void>((resolve, reject) => {
      editOrders(
        order?.order?.id,
        warehouseId,
        vehicleId,
        status,
        amount_upi,
        amount_cash,
        remarks,
        (response) => {
          toast.success("Order status updated successfully");
          resolve();
        },
        (error) => {
          toast.error(error || "Failed to update order status");
          reject(error);
        }
      );
    });
  };

  const handleBack = () => {
    window.history.back();
  };

  const paidAmount = parseFloat(amountPaid) || 0;
  const balance = selectedItemsTotal - paidAmount;
  const returnAmount = paidAmount - selectedItemsTotal;
  const showPaymentOptions = paymentType !== 'Prepaid' && (someItemsChecked || allItemsChecked);
  const isVehicleAssigned = order?.order?.status && (order?.order?.status === 'vehicle assigned' || order?.order?.status === 'OFD' || order?.order?.status.toLowerCase()=== 'attempted');
  if (itemsLoading) {
    return (
      <Box p={2} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading items...</Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Box sx={{ marginTop: '30px' }}>
        <Heading title="Order Summary" onBackClick={handleBack} />
      </Box>

      <Paper elevation={3} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ width: '30%' }}>Items</Typography>
          <Typography variant="subtitle2" sx={{ width: '15%', textAlign: 'center' }}>Qty</Typography>
          <Typography variant="subtitle2" sx={{ width: '20%', textAlign: 'center' }}>Rate</Typography>
          {isVehicleAssigned && <Box sx={{ width: '15%' }}></Box>}
        </Box>
        
        {items.length === 0 ? (
          <Typography sx={{ p: 2, textAlign: 'center' }}>No items found for this order</Typography>
        ) : (
          items.map((item) => (
            <Box
              key={item.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              sx={{
                backgroundColor: itemsChecked[item.id] ? '#f5f5f5' : 'transparent',
                borderRadius: 1,
                p: 1
              }}
            >
              <Typography sx={{ width: '30%' }}>{item.name}</Typography>
              <Typography sx={{ width: '15%', textAlign: 'center' }}>{item.qty}</Typography>
              <Typography sx={{ width: '20%', textAlign: 'center' }}>
                ₹{item.rate.toFixed(2)}
              </Typography>
              {isVehicleAssigned && (
                <Box sx={{ width: '15%', textAlign: 'right' }}>
                  <Checkbox
                    checked={itemsChecked[item.id]}
                    onChange={() => handleCheckboxChange(item.id)}
                    size="small"
                  />
                </Box>
              )}
            </Box>
          ))
        )}

        <Divider sx={{ my: 2 }} />

        {(someItemsChecked || allItemsChecked) ? (
          <>
            <Box display="flex" justifyContent="space-between" mt={1}>
              <Typography fontWeight="bold">Selected Items Total</Typography>
              <Typography fontWeight="bold" color="primary">
                ₹ {selectedItemsTotal.toFixed(2)}
              </Typography>
            </Box>
          </>
        ) : (
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight="bold">Order Total</Typography>
            <Typography fontWeight="bold">
              ₹ {totalAmount.toFixed(2)}
            </Typography>
          </Box>
        )}

        {paymentType === 'COD' && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="subtitle1">Choose Mode of Payment</Typography>
            <Typography
              sx={{
                backgroundColor: '#f88',
                color: '#fff',
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 500,
                fontSize: '0.9rem',
              }}
            >
              {paymentType}
            </Typography>
          </Box>
        )}

        {paymentType === 'Prepaid' && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Typography variant="subtitle1">Payment Mode</Typography>
            <Typography
              sx={{
                backgroundColor: 'rgba(4, 100, 4, 1)',
                color: '#fff',
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 500,
                fontSize: '0.9rem',
              }}
            >
              Prepaid
            </Typography>
          </Box>
        )}

        {showPaymentOptions && isVehicleAssigned && (
          <Box mt={3}>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={paymentMode.includes('Cash')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPaymentMode((prev) => prev + 'Cash ');
                      } else {
                        setPaymentMode((prev) => prev.replace('Cash ', ''));
                        setAmountPaid('');
                      }
                    }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <img src={cashIcon} width={24} alt="cash" />
                    <Box>
                      <Typography>Cash</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Collect Cash
                      </Typography>
                    </Box>
                  </Box>
                }
              />

              {paymentMode.includes('Cash') && (
                <Box pl={4} mt={1} mb={2}>
                  <TextField
                    label="Amount Paid"
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="number"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    inputProps={{ step: "0.01" }}
                  />
                  {amountPaid && (
                    <Box mt={1}>
                      {balance > 0 ? (
                        <Typography color="error">
                          Balance to be paid: ₹{balance.toFixed(2)}
                        </Typography>
                      ) : returnAmount > 0 ? (
                        <Typography color="success.main">
                          Amount to return: ₹{returnAmount.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography color="success.main">
                          Exact amount paid
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={paymentMode.includes('UPI')}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPaymentMode((prev) => prev + 'UPI ');
                      } else {
                        setPaymentMode((prev) => prev.replace('UPI ', ''));
                      }
                    }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <img src={upiIcon} width={24} alt="upi" />
                    <Box>
                      <Typography>Collect with UPI</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Collect by QR code
                      </Typography>
                      {balance <= 0 && !paymentMode.includes('Cash') && (
                        <Typography variant="caption" color="error">
                          No balance to collect with UPI
                        </Typography>
                      )}
                    </Box>
                  </Box>
                }
              />
            </Box>
          </Box>
        )}

        {isVehicleAssigned && (
          <>
            {(someItemsChecked || allItemsChecked) ? (
             <Button
  fullWidth
  variant="contained"
  color="success"
  sx={{ mt: 2 }}
  onClick={handlePartialDelivery}
  disabled={isLoading || (paymentMode === 'Cash' && balance > 0)}
>
  {isLoading
    ? "Processing..."
    : paymentMode === 'Cash' && balance > 0
    ? "Please collect full amount"
    : allItemsChecked
    ? "Delivered"
    : "Partially Delivered"}
</Button>

            ) : (
              <Button
                fullWidth
                variant="contained"
                color="error"
                sx={{ mt: 2 }}
                 onClick={() => {
    setModalType("unable");
    setModalOpen(true);
  }}
                disabled={isLoading}
              >
                Attempted
              </Button>
            )}
          </>
        )}
      </Paper>
      
      {isVehicleAssigned && (
        <UnableToDeliverModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSubmit={handleUnableToDeliverSubmit}
          reason={reason}
          setReason={setReason}
          type={modalType || "unable"} 
        />
      )}
    </Box>
  );
};

export default OrderSummaryScreen;